import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, Loader2, Package, RefreshCw } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import * as ozon from "@/api/ozon_fbs";
import { Button } from "@/components/ui/button";
import { toastError } from "@/lib/api-errors";
import { LoadState } from "@/lib/scan-ui/screen";
import { piecesWord } from "@/lib/fbs";

/**
 * Главный экран ФБС Ozon: сводка, плитки кабинетов, обновление отправлений.
 *
 * ⚠️ Списка отправлений и поиска по ним здесь НЕТ — сняты 11.09.2026. Список
 * был остатком этапа B, когда плиток ещё не существовало и он был единственным
 * содержимым экрана. После появления плиток он не отвечал ни на один вопрос,
 * которого нет на скан-экране: «что нести» показывает список товаров, «что
 * паковать» — шторка «Коробки», «есть ли товар не из 1С» — бейджи в обоих
 * местах. При одном отправлении он выглядел избыточным, при двухстах стал бы
 * стеной номеров. У ВБ на `/fbs` такого списка нет вовсе.
 *
 * ⚠️ Поиск снят по той же причине, хотя выглядел полезнее. Его единственный
 * правдоподобный сценарий — «нашли наклейку, что это за отправление» — не
 * работает: скан-экран Озона объявляет только товарные символики
 * (`GOODS_FORMATS`), QR отправления он не видит; а набирать номер руками идут
 * в кабинет продавца, где есть вся история, а не наше окно в три недели.
 * Понадобится отвечать на «что это за номер» — это будет запрос В OZON по
 * номеру, а не фильтр по нашей базе.
 *
 * ⚠️ Плитка НЕ копирует вебешную дословно, и это не небрежность. У ВБ единица
 * работы — ПОСТАВКА: одна открытая на юрлицо, «собрано X из Y», «завершить».
 * У Ozon поставки нет вовсе, работают отправлениями, и они независимы.
 * Нарисовать здесь поставку значило бы показать сущность, которой не
 * существует, — а кладовщик поверит экрану, а не документации.
 *
 * Общее берём: сводка сверху, кабинет как вход, счётчики считает СЕРВЕР.
 */
export function OzonHome(): React.ReactElement {
  const qc = useQueryClient();
  const overviewQ = useQuery({ queryKey: ["ozon", "overview"], queryFn: ozon.overview });

  const sync = useMutation({
    mutationFn: ozon.syncPostings,
    onSuccess: (data) => {
      const failed = data.cabinets.filter((c) => c.error);
      const fetched = data.cabinets.reduce((n, c) => n + c.fetched, 0);
      if (failed.length) {
        // Отказ по кабинету — не тост, который уедет: его надо прочитать.
        failed.forEach((c) => toast.error(`${c.cabinet}: ${c.error}`, { duration: 30_000 }));
      } else {
        const gone = data.cabinets.reduce((n, c) => n + c.gone, 0);
        toast.success(
          `Обновлено. Отправлений: ${fetched}` + (gone ? `, погашено: ${gone}` : ""),
        );
      }
      const unresolved = data.cabinets.flatMap((c) => c.unresolved_skus);
      if (unresolved.length) {
        toast.warning(
          `Не опознано в 1С: ${unresolved.length} SKU (${unresolved.slice(0, 3).join(", ")}…)`,
          { duration: 30_000 },
        );
      }
      void qc.invalidateQueries({ queryKey: ["ozon"] });
    },
    onError: toastError("Не удалось выполнить"),
  });

  const data = overviewQ.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">ФБС (Ozon)</h1>
          <p className="text-sm text-muted-foreground">
            {data
              ? `К сбору: ${data.postings} · осталось ${data.units_left} шт из ${data.units_total}`
              : "Ozon FBS"}
          </p>
        </div>
        <Button onClick={() => sync.mutate()} disabled={sync.isPending}>
          {sync.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 h-4 w-4" />
          )}
          Обновить отправления
        </Button>
      </div>

      <CabinetTiles overviewQ={overviewQ} />
    </div>
  );
}

function CabinetTiles({
  overviewQ,
}: {
  overviewQ: ReturnType<typeof useQuery<ozon.OzonOverview>>;
}): React.ReactElement {
  const navigate = useNavigate();
  const data = overviewQ.data;
  if (overviewQ.isLoading || overviewQ.isError) {
    return (
      <LoadState
        isLoading={overviewQ.isLoading}
        error={overviewQ.error}
        onRetry={() => overviewQ.refetch()}
        className="flex flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center"
      />
    );
  }

  if (!data?.cabinets.length) {
    return (
      <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        Кабинеты Ozon не настроены. Заведите их в «Системные настройки → ФБС
        (Ozon)» — без ключей и склада продавца отправления не подтянутся.
      </p>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {data.cabinets.map((c) => (
        /* ⚠️ Вход в завершение — ОТДЕЛЬНОЙ строкой под плиткой, а не внутри
           неё: плитка сама кнопка, и вложить в неё вторую нельзя. Разметка
           повторяет ВБ — там этот вход живёт ровно так же. */
        <div key={c.cabinet_id} className="rounded-xl border">
          <CabinetTile
            cabinet={c}
            onOpen={() => navigate(`/ozon/cabinets/${c.cabinet_id}/pick`)}
          />
          {c.supply_id && c.supply_postings > 0 && (
            <div className="border-t px-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between"
                onClick={() => navigate(`/ozon/supplies/${c.supply_id}/finish`)}
              >
                Завершение поставки · {c.supply_postings}
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CabinetTile({
  cabinet,
  onOpen,
}: {
  cabinet: ozon.OzonCabinetOverview;
  onOpen: () => void;
}): React.ReactElement {
  // Считает сервер: перебор достижим, и вычитание тут дало бы минус.
  const left = cabinet.units_left;
  // Идти на сбор, когда собирать нечего, незачем: любой скан там упрётся в
  // «этот товар не нужен ни в одном отправлении». Сначала «Обновить».
  const nothingToDo = cabinet.postings === 0;
  // ⚠️ Ненастроенный кабинет ОТКЛЮЧАЕТ вход, а не молча отдаёт пустой экран:
  // у ВБ этот урок записан ценой похода кладовщика к стеллажу впустую.
  const blocked = !cabinet.keys_ready
    ? "не задан слаг Infisical — ключи читать неоткуда"
    : !cabinet.warehouse_ready
      ? "не выбран склад 1С — списывать будет неоткуда"
      : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={Boolean(blocked) || nothingToDo}
      className="w-full rounded-xl p-3 text-left transition enabled:hover:bg-accent disabled:opacity-60"
    >
      <div className="flex items-center justify-between">
        <span className="text-lg font-semibold">{cabinet.name}</span>
        {!blocked && !nothingToDo && (
          <ChevronRight className="size-5 text-muted-foreground" />
        )}
      </div>

      {cabinet.warehouse_name && (
        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
          <Package className="size-3" />
          {cabinet.warehouse_name}
        </div>
      )}

      <div className="mt-1 text-sm">
        {blocked ? (
          <span className="text-destructive">{blocked}</span>
        ) : nothingToDo ? (
          "Отправлений к сбору нет"
        ) : (
          <>
            К сбору: <b>{cabinet.postings}</b>{" "}
            {postingsWord(cabinet.postings)} · осталось <b>{left}</b>{" "}
            {piecesWord(left)}
            {cabinet.ready > 0 && (
              <span className="text-emerald-700"> · готово {cabinet.ready}</span>
            )}
          </>
        )}
      </div>

      {/* Собрать такое отправление нечем — узнать об этом надо здесь, а не
          когда сканер уже поднесён к коробке. */}
      {cabinet.unresolved > 0 && (
        <div className="mt-1 flex items-center gap-1 text-xs text-destructive">
          <AlertTriangle className="size-3" />
          {cabinet.unresolved} {postingsWord(cabinet.unresolved)} с товаром, не
          опознанным в 1С
        </div>
      )}
    </button>
  );
}

const postingsWord = (n: number): string => {
  const m100 = n % 100;
  const m10 = n % 10;
  if (m100 >= 11 && m100 <= 14) return "отправлений";
  if (m10 === 1) return "отправление";
  if (m10 >= 2 && m10 <= 4) return "отправления";
  return "отправлений";
};
